import { Technology } from '@common/technology.enum';
import { LibrarySpecimenInput } from '../base/library-specimen.model';
import { BatchInfo } from '../shared/batch-info.model';

/**
 * Base interface for all library creation commands
 * Contains fields common to all technologies
 */
export interface CreateLibraryCommandBase {
  /** Technology type - determines which command subtype is valid */
  readonly technology: Technology;

  /** Library code (e.g., LIB-001) */
  readonly libraryCode: string;

  /** Compatibility-only legacy field (not required for request sending) */
  readonly createdByUserId?: string;

  /** Specimens to assign to the library */
  readonly specimens: LibrarySpecimenInput[];
}

/**
 * Command to create an NGS library
 */
export interface CreateLibraryNGSCommand extends CreateLibraryCommandBase {
  readonly technology: Technology.NGS;

  /** Cell identifier (required for NGS) */
  readonly cellId: string;

  /** Plate identifier (optional) */
  readonly plateId?: string;

  /** Index identifier (optional) */
  readonly indexId?: string;
}

/**
 * Command to create a Microarray library
 */
export interface CreateLibraryMicroarrayCommand
  extends CreateLibraryCommandBase {
  readonly technology: Technology.MICROARREGLOS;

  /** Chip type (required for Microarray) */
  readonly chipType: string;

  /** Arrangement type (required for Microarray) */
  readonly arrangementType: string;

  /** Plate name (optional) */
  readonly plateName?: string;

  /** Batch information (optional) */
  readonly batchInfo?: BatchInfo;
}

/**
 * Command to create an MLPA library
 */
export interface CreateLibraryMLPACommand extends CreateLibraryCommandBase {
  readonly technology: Technology.MLPA;

  /** Creation date (optional) */
  readonly creationDate?: Date;

  /** Plate name (optional) */
  readonly plateName?: string;

  /** Equipment name (optional) */
  readonly equipment?: string;

  /** Batch information (optional) */
  readonly batchInfo?: BatchInfo;
}

/**
 * Command to create a Triplet Expansion library
 */
export interface CreateLibraryTripletExpansionCommand
  extends CreateLibraryCommandBase {
  readonly technology: Technology.EXPANSION_TRIPLETAS;

  /** Creation date (optional) */
  readonly creationDate?: Date;

  /** Plate name (optional) */
  readonly plateName?: string;

  /** Equipment name (optional) */
  readonly equipment?: string;

  /** Batch information (optional) */
  readonly batchInfo?: BatchInfo;
}

/**
 * Union type for all library creation commands
 */
export type CreateLibraryCommand =
  | CreateLibraryNGSCommand
  | CreateLibraryMicroarrayCommand
  | CreateLibraryMLPACommand
  | CreateLibraryTripletExpansionCommand;

/**
 * Type guard for NGS command
 */
export function isCreateLibraryNGSCommand(
  command: CreateLibraryCommand
): command is CreateLibraryNGSCommand {
  return command.technology === Technology.NGS;
}

/**
 * Type guard for Microarray command
 */
export function isCreateLibraryMicroarrayCommand(
  command: CreateLibraryCommand
): command is CreateLibraryMicroarrayCommand {
  return command.technology === Technology.MICROARREGLOS;
}

/**
 * Type guard for MLPA command
 */
export function isCreateLibraryMLPACommand(
  command: CreateLibraryCommand
): command is CreateLibraryMLPACommand {
  return command.technology === Technology.MLPA;
}

/**
 * Type guard for Triplet Expansion command
 */
export function isCreateLibraryTripletExpansionCommand(
  command: CreateLibraryCommand
): command is CreateLibraryTripletExpansionCommand {
  return command.technology === Technology.EXPANSION_TRIPLETAS;
}

/**
 * Validates a library creation command
 * @throws Error if validation fails
 */
export function validateCreateLibraryCommand(
  command: CreateLibraryCommand
): void {
  if (!command.libraryCode?.trim()) {
    throw new Error('Library code is required');
  }

  if (!command.specimens || command.specimens.length === 0) {
    throw new Error('At least one specimen is required');
  }

  if (isCreateLibraryNGSCommand(command) && !command.cellId?.trim()) {
    throw new Error('Cell ID is required for NGS libraries');
  }

  if (isCreateLibraryMicroarrayCommand(command)) {
    if (!command.chipType?.trim()) {
      throw new Error('Chip type is required for Microarray libraries');
    }
    if (!command.arrangementType?.trim()) {
      throw new Error('Arrangement type is required for Microarray libraries');
    }
  }
}
